import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DialogActions from "@material-ui/core/DialogActions";
import MuiDialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import Button from "@material-ui/core/Button";

import { getDepopUser } from "src/utils/extension/depop";
import type { Cookie } from "src/utils/extension";
import {
  getDepopIntegration,
  depopIntegrationCreate,
} from "src/apiService/modules/depop";
import { AddNewSalePlatformIntegrationButton } from "src/components/AddNewSalePlatformIntegrationDialog";

import { StatefulLoadableButton } from "src/components/LoadableButton";
import DialogTitle from "./DialogTitle";
import {
  completeUserSetupQuestionCardPlatform,
  doesUserHaveCardPlatform,
} from "src/apiService/modules/users";
import { useDispatch } from "react-redux";
import { depopSyncSales } from "src/store/integrationSync/actions/depop";
import MonthYear from "src/interfaces/monthYear.interface";
import { useRecordEvent } from "src/hooks/useRecordEvent";

interface DepopConfirmProps {
  onClose: () => void;
  showConnectSalePlatform?: boolean;
}

function DepopConfirm({
  onClose,
  showConnectSalePlatform = true,
}: DepopConfirmProps) {
  const dispatch = useDispatch();
  const recordEvent = useRecordEvent();
  const [depopUserData, setDepopUserData] = useState<
    | {
        username: string;
        userId: string;
        cookies: Cookie[];
      }
    | { error: string }
    | { loading: true }
  >({ loading: true });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const data = await getDepopUser();
        if (cancel) return;
        setDepopUserData(data);
      } catch (e) {
        if (cancel) return;
        setDepopUserData({ error: (e as Error).message });
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const onConnect = async (bankAccountId?: string) => {
    if ("cookies" in depopUserData && depopUserData.cookies) {
      const existingDepopIntegration = await getDepopIntegration();
      if (existingDepopIntegration?.id) {
        if (await doesUserHaveCardPlatform("Depop")) {
          await completeUserSetupQuestionCardPlatform("Depop");
          const currentDate = new Date();
          dispatch(
            depopSyncSales(existingDepopIntegration.id, {
              start: {
                month: 1,
                year: currentDate.getFullYear() - 1,
              },
              end: {
                month: currentDate.getMonth() + 1,
                year: currentDate.getFullYear(),
              } as MonthYear,
            })
          );
        }
        toast.info("That Depop account is already connected.");
        onClose();
        return;
      }

      await depopIntegrationCreate({
        cookies: depopUserData.cookies,
        username: depopUserData.username,
        userId: depopUserData.userId,
        lastSync: new Date().toISOString(),
        bankAccountId,
      });
      recordEvent({
        type: "feature-adoption",
        feature: "integration-added",
        element: "sale-platform-depop",
      });
      if (await doesUserHaveCardPlatform("Depop")) {
        const depopIntegration = await getDepopIntegration();
        await completeUserSetupQuestionCardPlatform("Depop");
        if (depopIntegration?.id) {
          const currentDate = new Date();
          dispatch(
            depopSyncSales(depopIntegration.id, {
              start: {
                month: 1,
                year: currentDate.getFullYear() - 1,
              },
              end: {
                month: currentDate.getMonth() + 1,
                year: currentDate.getFullYear(),
              } as MonthYear,
            })
          );
        }
      }
      onClose();
    }
  };

  return (
    <>
      <DialogTitle onClose={onClose}>Add Depop integration</DialogTitle>
      <MuiDialogContent>
        <DialogContentText>
          {"username" in depopUserData
            ? `The Depop account "${depopUserData.username}" will be added.`
            : "error" in depopUserData
            ? `ERROR: ${depopUserData.error}`
            : "Loading..."}
        </DialogContentText>
      </MuiDialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {showConnectSalePlatform ? (
          <AddNewSalePlatformIntegrationButton
            variant="contained"
            disabled={
              !!(depopUserData as any).error || (depopUserData as any).loading
            }
            onConnect={onConnect}
            color="primary"
            autoFocus
          >
            Add
          </AddNewSalePlatformIntegrationButton>
        ) : (
          <StatefulLoadableButton
            color="primary"
            variant="contained"
            disabled={
              !!(depopUserData as any).error || (depopUserData as any).loading
            }
            onClick={async () => await onConnect()}
          >
            Connect
          </StatefulLoadableButton>
        )}
      </DialogActions>
    </>
  );
}

export default DepopConfirm;
